// src/components/ajuda/TourGuiado.js
// Tour com balões: destaca um elemento da tela por vez.
// Não usa biblioteca externa. Os passos ficam em ajudaConteudo.js.

import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import "./Ajuda.css";

const MARGEM = 12;
const FOLGA_DESTAQUE = 6;

function elementoVisivel(el) {
  if (!el) return false;
  const r = el.getBoundingClientRect();
  if (r.width === 0 || r.height === 0) return false;
  const estilo = window.getComputedStyle(el);
  return estilo.visibility !== "hidden" && estilo.display !== "none";
}

export function encontrarAlvo(alvo) {
  if (!alvo) return null;
  const seletores = Array.isArray(alvo) ? alvo : [alvo];
  for (const seletor of seletores) {
    let encontrados = [];
    try {
      encontrados = document.querySelectorAll(seletor);
    } catch {
      encontrados = [];
    }
    for (const el of encontrados) {
      if (elementoVisivel(el)) return el;
    }
  }
  return null;
}

function reduzirMovimento() {
  return window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
}

function alturaBarraInferior() {
  const valor = getComputedStyle(document.documentElement).getPropertyValue("--tk-bottomnav-h");
  return parseInt(valor, 10) || 0;
}

export default function TourGuiado({ passos, aberto, onFechar }) {
  const [ativos, setAtivos] = useState([]);
  const [indice, setIndice] = useState(0);
  const [rect, setRect] = useState(null);
  const [posicao, setPosicao] = useState({ top: -9999, left: -9999 });

  const alvoRef = useRef(null);
  const balaoRef = useRef(null);
  const avancarRef = useRef(null);
  const frameRef = useRef(null);
  const onFecharRef = useRef(onFechar);
  onFecharRef.current = onFechar;
  const fechar = useCallback((concluiu) => onFecharRef.current?.(concluiu), []);

  // Ao abrir: mantém só os passos cujo alvo existe na tela
  useEffect(() => {
    if (!aberto) return;
    const lista = (passos || []).filter((p) => !p.alvo || encontrarAlvo(p.alvo));
    if (!lista.length) {
      fechar(false);
      return;
    }
    setAtivos(lista);
    setIndice(0);
  }, [aberto, passos, fechar]);

  const passo = ativos[indice];
  const total = ativos.length;
  const ultimo = indice === total - 1;

  const medir = useCallback(() => {
    const el = alvoRef.current;
    if (!el || !document.body.contains(el)) {
      setRect(null);
      return;
    }
    const r = el.getBoundingClientRect();
    setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
  }, []);

  const agendarMedida = useCallback(() => {
    if (frameRef.current) cancelAnimationFrame(frameRef.current);
    frameRef.current = requestAnimationFrame(medir);
  }, [medir]);

  // Troca de passo: localiza o alvo e rola até ele
  useLayoutEffect(() => {
    if (!aberto || !passo) return;
    const el = passo.alvo ? encontrarAlvo(passo.alvo) : null;
    alvoRef.current = el;
    if (el) {
      el.scrollIntoView({ block: "center", behavior: reduzirMovimento() ? "auto" : "smooth" });
    }
    medir();
    const t = setTimeout(medir, 400); // depois da rolagem suave
    return () => clearTimeout(t);
  }, [aberto, passo, medir]);

  // Acompanha rolagem e redimensionamento
  useEffect(() => {
    if (!aberto) return;
    window.addEventListener("scroll", agendarMedida, true);
    window.addEventListener("resize", agendarMedida);
    return () => {
      window.removeEventListener("scroll", agendarMedida, true);
      window.removeEventListener("resize", agendarMedida);
      if (frameRef.current) cancelAnimationFrame(frameRef.current);
    };
  }, [aberto, agendarMedida]);

  // Posiciona o balão perto do alvo, sem sair da tela
  useLayoutEffect(() => {
    if (!aberto || !passo) return;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const balao = balaoRef.current;
    const bw = balao ? balao.offsetWidth : Math.min(380, vw - MARGEM * 2);
    const bh = balao ? balao.offsetHeight : 220;
    const limiteInferior = vh - alturaBarraInferior() - MARGEM;

    if (!rect) {
      setPosicao({
        top: Math.max(MARGEM, (vh - bh) / 2),
        left: Math.max(MARGEM, (vw - bw) / 2),
      });
      return;
    }

    const baseAlvo = rect.top + rect.height + FOLGA_DESTAQUE + MARGEM;
    const topoAlvo = rect.top - FOLGA_DESTAQUE - MARGEM;
    let top;
    if (baseAlvo + bh <= limiteInferior) top = baseAlvo;
    else if (topoAlvo - bh >= MARGEM) top = topoAlvo - bh;
    else top = Math.max(MARGEM, limiteInferior - bh);

    const left = Math.min(Math.max(rect.left, MARGEM), vw - bw - MARGEM);
    setPosicao({ top, left });
  }, [aberto, passo, rect]);

  // Foco no botão principal a cada passo
  useEffect(() => {
    if (aberto && passo) avancarRef.current?.focus({ preventScroll: true });
  }, [aberto, passo]);

  const avancar = useCallback(() => {
    if (ultimo) fechar(true);
    else setIndice((i) => Math.min(i + 1, total - 1));
  }, [ultimo, total, fechar]);

  const voltar = useCallback(() => setIndice((i) => Math.max(0, i - 1)), []);
  const pular = useCallback(() => fechar(false), [fechar]);

  // Teclado: Esc pula, setas navegam
  useEffect(() => {
    if (!aberto) return;
    const onKey = (e) => {
      if (e.key === "Escape") pular();
      if (e.key === "ArrowRight") avancar();
      if (e.key === "ArrowLeft") voltar();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [aberto, avancar, voltar, pular]);

  if (!aberto || !passo) return null;

  const destaque = rect
    ? {
        top: rect.top - FOLGA_DESTAQUE,
        left: rect.left - FOLGA_DESTAQUE,
        width: rect.width + FOLGA_DESTAQUE * 2,
        height: rect.height + FOLGA_DESTAQUE * 2,
      }
    : null;

  return createPortal(
    <div className="aj-tour" aria-live="polite">
      {/* Camada que bloqueia cliques na página durante o tour */}
      <div className="aj-tour-bloqueio" />
      {destaque ? (
        <div className="aj-tour-destaque" style={destaque} />
      ) : (
        <div className="aj-tour-escurecer" />
      )}

      <div
        ref={balaoRef}
        className="aj-tour-balao"
        role="dialog"
        aria-modal="true"
        aria-labelledby="aj-tour-titulo"
        aria-describedby="aj-tour-texto"
        style={{ top: posicao.top, left: posicao.left }}
      >
        <span className="aj-tour-contador">
          Passo {indice + 1} de {total}
        </span>
        <h2 id="aj-tour-titulo" className="aj-tour-titulo">
          {passo.titulo}
        </h2>
        <p id="aj-tour-texto" className="aj-tour-texto">
          {passo.texto}
        </p>

        <div className="aj-tour-pontos" aria-hidden="true">
          {ativos.map((_, i) => (
            <span key={i} className={i === indice ? "is-atual" : i < indice ? "is-feito" : ""} />
          ))}
        </div>

        <div className="aj-tour-acoes">
          <button type="button" className="aj-tour-pular" onClick={pular}>
            Pular tour
          </button>
          {indice > 0 && (
            <button type="button" className="tk-btn tk-btn--secundario" onClick={voltar}>
              Voltar
            </button>
          )}
          <button
            ref={avancarRef}
            type="button"
            className="tk-btn tk-btn--primario"
            onClick={avancar}
          >
            {ultimo ? "Concluir" : "Próximo"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
